import React, { useState } from 'react';
import AppHeader from '../../../components/AppHeader.tsx';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendarCheck, faUser, faClock, faSearch, faPlus, faSave, faTimes } from '@fortawesome/free-solid-svg-icons';
import { useStaffLoV } from '@/features/services/hooks/useStaffLoV';
import { useServicesLoV } from '@/features/services/hooks/useServicesLoV';

const NewReservationPage: React.FC = () => {
  const [selectedCustomer, setSelectedCustomer] = useState('');
  const [selectedStaff, setSelectedStaff] = useState('');
  const [selectedService, setSelectedService] = useState('');
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedTime, setSelectedTime] = useState('');
  
  const { staff: staffList, isLoading: isLoadingStaff } = useStaffLoV(true);
  const { services: servicesList, isLoading: isLoadingServices } = useServicesLoV(true);

  const breadcrumb = [{ label: 'Rezervace' }, { label: 'Nová rezervace' }];

  const customers = [
    { id: '1', name: 'Jana Nováková', phone: '+420 123 456 789', email: 'jana.novakova@email.cz' },
    { id: '2', name: 'Petr Svoboda', phone: '+420 987 654 321', email: 'petr.svoboda@email.cz' },
    { id: '3', name: 'Marie Krásná', phone: '+420 555 666 777', email: 'marie.krasna@email.cz' },
  ];


  const timeSlots = [
    '09:00',
    '09:30',
    '10:00',
    '10:30',
    '11:00',
    '11:30',
    '12:00',
    '12:30',
    '13:00',
    '13:30',
    '14:00',
    '14:30',
    '15:00',
    '15:30',
    '16:00',
    '16:30',
    '17:00',
    '17:30',
  ];

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Handle form submission
    console.log('Creating reservation:', {
      customer: selectedCustomer,
      staff: selectedStaff,
      service: selectedService,
      date: selectedDate,
      time: selectedTime,
    });
  };

  return (
    <div className="h-full flex flex-col">
      <AppHeader title="Nová rezervace" breadcrumb={breadcrumb} />

      <div className="flex-1 overflow-y-auto bg-gray-50">
        <div className="p-6">
          <div className="max-w-4xl mx-auto">
            <div className="bg-white rounded-lg shadow-sm">
              <div className="p-6 border-b border-gray-200">
                <h2 className="text-xl font-semibold text-gray-900">Nová rezervace</h2>
              </div>

              <form onSubmit={handleSubmit} className="p-6 space-y-6">
                {/* Customer Selection */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <FontAwesomeIcon icon={faUser} className="mr-2" />
                    Zákazník
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      placeholder="Vyhledat zákazníka..."
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500/20 focus:border-green-500"
                    />
                    <FontAwesomeIcon icon={faSearch} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                  </div>
                  <div className="mt-2 space-y-2">
                    {customers.map(customer => (
                      <div
                        key={customer.id}
                        className={`p-3 border rounded-lg cursor-pointer transition-colors ${
                          selectedCustomer === customer.id ? 'border-green-500 bg-green-50' : 'border-gray-200 hover:bg-gray-50'
                        }`}
                        onClick={() => setSelectedCustomer(customer.id)}
                      >
                        <p className="font-medium text-gray-900">{customer.name}</p>
                        <p className="text-sm text-gray-600">{customer.phone}</p>
                      </div>
                    ))}
                  </div>
                  <button type="button" className="mt-2 flex items-center text-green-600 hover:text-green-800">
                    <FontAwesomeIcon icon={faPlus} className="w-4 h-4 mr-1" />
                    Přidat nového zákazníka
                  </button>
                </div>

                {/* Service Selection */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <FontAwesomeIcon icon={faCalendarCheck} className="mr-2" />
                    Služba
                  </label>
                  <select
                    value={selectedService}
                    onChange={e => setSelectedService(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500/20 focus:border-green-500"
                    disabled={isLoadingServices}
                  >
                    <option value="">Vyberte službu</option>
                    {servicesList.map(service => (
                      <option key={service.id} value={service.id}>
                        {service.name}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Staff Selection */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <FontAwesomeIcon icon={faUser} className="mr-2" />
                    Zaměstnanec
                  </label>
                  <select
                    value={selectedStaff}
                    onChange={e => setSelectedStaff(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500/20 focus:border-green-500"
                    disabled={isLoadingStaff}
                  >
                    <option value="">Vyberte zaměstnance</option>
                    {staffList.map(member => (
                      <option key={member.id} value={member.id}>
                        {member.name}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Date and Time */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <FontAwesomeIcon icon={faCalendarCheck} className="mr-2" />
                      Datum
                    </label>
                    <input
                      type="date"
                      value={selectedDate}
                      onChange={e => setSelectedDate(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500/20 focus:border-green-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <FontAwesomeIcon icon={faClock} className="mr-2" />
                      Čas
                    </label>
                    <select
                      value={selectedTime}
                      onChange={e => setSelectedTime(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500/20 focus:border-green-500"
                    >
                      <option value="">Vyberte čas</option>
                      {timeSlots.map(time => (
                        <option key={time} value={time}>
                          {time}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Notes */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Poznámky</label>
                  <textarea
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500/20 focus:border-green-500"
                    placeholder="Přidejte poznámky k rezervaci..."
                  />
                </div>

                {/* Action Buttons */}
                <div className="flex items-center justify-end space-x-4 pt-6 border-t border-gray-200">
                  <button type="button" className="flex items-center px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors">
                    <FontAwesomeIcon icon={faTimes} className="w-4 h-4 mr-2" />
                    Zrušit
                  </button>
                  <button type="submit" className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors">
                    <FontAwesomeIcon icon={faSave} className="w-4 h-4 mr-2" />
                    Vytvořit rezervaci
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NewReservationPage;
